// FieldContext.tsx
import {
  FormControl,
  FormControlProps,
  FormErrorMessage,
  FormErrorMessageProps
} from "@chakra-ui/react"
import { FieldAttributes, Field as FormikField, useFormikContext } from "formik"
import { createContext, forwardRef, useContext } from "react"

// FormField Context
interface FormFieldContextValue {
  name: string
  isValid: boolean
  error?: string
}

const FormFieldContext = createContext<FormFieldContextValue | null>(null)

export const useFormFieldContext = () => {
  const context = useContext(FormFieldContext)
  if (!context) {
    throw new Error(
      "useFormFieldContext must be used within a FormFieldProvider"
    )
  }
  return context
}

interface FormFieldProviderProps {
  name: string
  children: React.ReactNode
}

// Responsible for providing the FormField context to avoid calculating these values for
// each FormField instance.
const FormFieldProvider: React.FC<FormFieldProviderProps> = ({
  name,
  children
}) => {
  const form = useFormikContext<Record<string, any>>()

  const isInvalid = Boolean(
    !!form.errors[name] &&
      form.touched[name] &&
      form.values[name] !== form.initialValues[name]
  )

  const value: FormFieldContextValue = {
    name,
    isValid: !isInvalid,
    error: isInvalid ? (form.errors[name] as string) : undefined
  }

  return (
    <FormFieldContext.Provider value={value}>
      {children}
    </FormFieldContext.Provider>
  )
}

// Scoped FormField Control
const FormFieldControl: React.FC<FormControlProps> = (props) => {
  const { isValid } = useFormFieldContext()

  return <FormControl isInvalid={!isValid} {...props} />
}

// Scoped Form Error Message
const FormFieldErrorMessage: React.FC<FormErrorMessageProps> = (props) => {
  const { error } = useFormFieldContext()

  return <FormErrorMessage {...props}>{error}</FormErrorMessage>
}

// A FormField component that wraps the formik FormField component.
const FormField: React.FC<
  FieldAttributes<any> & { children?: React.ReactNode }
> = forwardRef(({ children, ...props }, ref) => {
  const { name } = useFormFieldContext()

  return <FormikField ref={ref} id={name} variant="filled" {...props} />
})

export default {
  Context: FormFieldContext,
  Provider: FormFieldProvider,
  Field: FormField,
  Control: FormFieldControl,
  ErrorMessage: FormFieldErrorMessage
}
